import './SegmentedTabs.css';

export interface SegmentedTabOption<T extends string> {
  value: T;
  label: string;
  /** 先頭アイコン (SVG)。mask で抜いてタブのテキスト色に追従させる。 */
  iconMaskUrl?: string;
  tooltip?: string;
  disabled?: boolean;
}

interface SegmentedTabsProps<T extends string> {
  options: ReadonlyArray<SegmentedTabOption<T>>;
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** true で横幅いっぱいに等分 (flex:1)。false (既定) は内容なりの幅でコンパクトに並べる。 */
  stretch?: boolean;
  /** 配色。'brand' (既定) はプライマリカラー、'neutral' はシルバーグレー系。 */
  tone?: 'brand' | 'neutral';
  className?: string;
  ariaLabel?: string;
}

/**
 * セグメンテッドなタブ切替。AI俳優の新規生成ステップ (人物生成/アップロード) と同じ
 * ブランドカラーのアクティブ表示・角丸枠デザインを共通化したもの。
 * 2値の ON/OFF は ToggleSwitch、純粋なプロバイダ選択 (控えめ表示) は
 * ProviderSegmentedControl を使い分ける。
 */
export function SegmentedTabs<T extends string>({
  options,
  value,
  onChange,
  disabled,
  stretch,
  tone = 'brand',
  className,
  ariaLabel,
}: SegmentedTabsProps<T>) {
  return (
    <div
      className={`segmented-tabs ${stretch ? 'segmented-tabs--stretch' : ''} ${tone === 'neutral' ? 'segmented-tabs--neutral' : ''} ${className || ''}`}
      role="tablist"
      aria-label={ariaLabel}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            className={`segmented-tab ${active ? 'segmented-tab--active' : ''}`}
            onClick={() => { if (!active) onChange(opt.value); }}
            disabled={disabled || opt.disabled}
            title={opt.tooltip}
          >
            {opt.iconMaskUrl && (
              <span
                className="segmented-tab-icon"
                style={{ WebkitMaskImage: `url(${opt.iconMaskUrl})`, maskImage: `url(${opt.iconMaskUrl})` }}
                aria-hidden="true"
              />
            )}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
